/**
 * asnOnePagerWorkbookBuilder.ts
 * Builds a real .xlsx workbook for the ASN Gap One Pager table using ExcelJS.
 *
 * Layout (no SPARC category band — ASN metrics are not category-grouped):
 *   row 1  → headers (grain labels + metric labels, bold 14, metric fill #F8FAFD)
 *   row 2  → Grand Total (fill #F3F7FD, font #003A7D bold)
 *   row 3+ → every sorted data row
 * Panes are frozen at xSplit = grain count and ySplit = 2.
 */

import ExcelJS from 'exceljs';
import { GrainKey, grainLabel } from '../asnGapLocationFacts';
import {
  AsnGroupedRow,
  AsnMetricColumn,
  AsnMetricKey,
} from '../constants/asnOnePagerConstants';

export interface BuildAsnWorkbookParams {
  readonly sheetName: string;
  readonly rows: AsnGroupedRow[];
  readonly grandTotalValues: Record<AsnMetricKey, number> | null;
  readonly columns: AsnMetricColumn[];
  readonly grainKeys: GrainKey[];
}

const HEADER_ROW_INDEX = 1;
const GRAND_TOTAL_ROW_INDEX = 2;

const GRAND_TOTAL_FILL_HEX = 'F3F7FD';
const GRAND_TOTAL_FONT_COLOR_HEX = '003A7D';
const METRIC_HEADER_FILL_HEX = 'F8FAFD';

const GRAIN_COL_WIDTH = 18;
const METRIC_COL_WIDTH = 14;

/** Grains whose values are naturally numeric and should not be text-formatted */
const NUMERIC_GRAIN_KEYS: GrainKey[] = ['year'];

const isNumericGrainKey = (key: GrainKey): boolean => NUMERIC_GRAIN_KEYS.includes(key);

const numberFormatFor = (col: AsnMetricColumn): string => {
  switch (col.fmt) {
    case 'pct2':
      return '0.00"%"';
    case 'num1':
      return '0.0';
    case 'int':
    default:
      return '0';
  }
};

const normalizeGrainValue = (key: GrainKey, value: string | undefined): string | number => {
  if (!value) return '';
  if (!isNumericGrainKey(key)) return value;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : value;
};

const buildDataRow = (
  row: AsnGroupedRow,
  grainKeys: GrainKey[],
  columns: AsnMetricColumn[],
): Array<string | number | null> => [
  ...grainKeys.map((key) => normalizeGrainValue(key, row.grainVals[key])),
  ...columns.map((col) => {
    const value = row.vals[col.id];
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
  }),
];

export const buildAsnOnePagerWorkbook = ({
  sheetName,
  rows,
  grandTotalValues,
  columns,
  grainKeys,
}: BuildAsnWorkbookParams): ExcelJS.Workbook => {
  const grainCount = grainKeys.length;
  const totalCols = grainCount + columns.length;

  const workbook = new ExcelJS.Workbook();
  const ws = workbook.addWorksheet(sheetName, {
    views: [{ state: 'frozen', xSplit: grainCount, ySplit: grandTotalValues ? GRAND_TOTAL_ROW_INDEX : HEADER_ROW_INDEX }],
  });

  // ─── Row 1: headers ───────────────────────────────────────────────────
  const headerRow: Array<string | number | null> = [
    ...grainKeys.map((key) => grainLabel(key)),
    ...columns.map((col) => col.label),
  ];
  const sheetRows: Array<Array<string | number | null>> = [headerRow];

  // ─── Row 2: Grand Total ───────────────────────────────────────────────
  if (grandTotalValues) {
    const totalRow: Array<string | number | null> = Array(totalCols).fill('');
    if (grainCount > 0) totalRow[0] = 'Grand Total';
    columns.forEach((col, idx) => {
      const value = grandTotalValues[col.id];
      totalRow[grainCount + idx] = typeof value === 'number' && Number.isFinite(value) ? value : null;
    });
    sheetRows.push(totalRow);
  }

  // ─── Rows 3+: data ────────────────────────────────────────────────────
  rows.forEach((row) => sheetRows.push(buildDataRow(row, grainKeys, columns)));

  sheetRows.forEach((row) => ws.addRow(row));

  ws.columns = [
    ...grainKeys.map(() => ({ width: GRAIN_COL_WIDTH })),
    ...columns.map(() => ({ width: METRIC_COL_WIDTH })),
  ];

  // ─── Styling helpers ──────────────────────────────────────────────────
  const styleRow = (rowIndex: number, fillHex?: string, fontColorHex?: string) => {
    for (let colIndex = 1; colIndex <= totalCols; colIndex++) {
      const cell = ws.getCell(rowIndex, colIndex);
      cell.font = {
        ...(cell.font || {}),
        bold: true,
        size: 14,
        ...(fontColorHex ? { color: { argb: `FF${fontColorHex}` } } : {}),
      };
      if (fillHex) {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: `FF${fillHex}` } };
      }
    }
  };

  styleRow(HEADER_ROW_INDEX);
  for (let colIndex = grainCount + 1; colIndex <= totalCols; colIndex++) {
    ws.getCell(HEADER_ROW_INDEX, colIndex).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: `FF${METRIC_HEADER_FILL_HEX}` },
    };
  }
  if (grandTotalValues) {
    styleRow(GRAND_TOTAL_ROW_INDEX, GRAND_TOTAL_FILL_HEX, GRAND_TOTAL_FONT_COLOR_HEX);
  }

  // Center every metric cell and stamp the per-column number format
  const firstDataRow = grandTotalValues ? GRAND_TOTAL_ROW_INDEX : HEADER_ROW_INDEX;
  for (let rowIndex = firstDataRow; rowIndex <= sheetRows.length; rowIndex++) {
    for (let colIndex = 0; colIndex < columns.length; colIndex++) {
      const cell = ws.getCell(rowIndex, grainCount + colIndex + 1);
      cell.alignment = { ...(cell.alignment || {}), horizontal: 'center', vertical: 'middle' };
      if (typeof cell.value === 'number') {
        cell.numFmt = numberFormatFor(columns[colIndex]);
      }
    }
  }

  // Body rows keep a smaller font than the header / total rows
  const firstBodyRow = (grandTotalValues ? GRAND_TOTAL_ROW_INDEX : HEADER_ROW_INDEX) + 1;
  for (let rowIndex = firstBodyRow; rowIndex <= sheetRows.length; rowIndex++) {
    for (let colIndex = 1; colIndex <= totalCols; colIndex++) {
      const cell = ws.getCell(rowIndex, colIndex);
      cell.font = { ...(cell.font || {}), size: 12 };
    }
  }

  return workbook;
};
